import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Loader2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentSelectorModal = ({ 
  isOpen, 
  onClose, 
  jobId, 
  companyId, 
  moduleType,
  userToken,
  onSuccess 
}) => {
  const [enabledAgents, setEnabledAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executedAgentIds, setExecutedAgentIds] = useState([]);
  
  // Background execution state
  const [executionId, setExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const pollingInterval = useRef(null);

  // Get token from localStorage if not provided via props
  const token = userToken || localStorage.getItem('token');

  useEffect(() => {
    if (isOpen && jobId && companyId) {
      loadEnabledAgents();
      loadExecutedAgents();
    }
    
    // Cleanup polling on unmount or close
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [isOpen, jobId, companyId]);

  // Polling for execution status with retry logic
  useEffect(() => {
    let errorCount = 0;
    const maxErrors = 5;
    
    if (executionId && executing) {
      pollingInterval.current = setInterval(async () => {
        try {
          const response = await axios.get(
            `${API}/companies/${companyId}/agent-executions/${executionId}/status`,
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000 // 10 second timeout for status check
            }
          );
          
          errorCount = 0; // Reset error count on success
          setExecutionStatus(response.data);
          
          // Check if execution is complete
          if (response.data.status === 'completed' || response.data.status === 'error') {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
            
            if (response.data.status === 'completed') {
              const totalInsights = response.data.insights_created?.length || 0;
              const totalErrors = response.data.errors?.length || 0;
              
              if (totalInsights > 0) {
                toast.success(`✅ ${totalInsights} insights creados exitosamente`);
              }
              if (totalErrors > 0) {
                toast.warning(`⚠️ ${totalErrors} agentes fallaron`);
              }
              
              if (onSuccess) {
                onSuccess();
              }
              
              // Reset state after a delay
              setTimeout(() => {
                setExecuting(false);
                setExecutionId(null);
                setExecutionStatus(null);
                onClose();
              }, 2000);
            } else {
              toast.error('Error en la ejecución de agentes');
              setExecuting(false);
            }
          }
        } catch (error) {
          errorCount++;
          console.error(`Error polling execution status (${errorCount}/${maxErrors}):`, error?.message);
          
          // After max errors, don't stop - just show a warning but continue polling
          if (errorCount >= maxErrors) {
            toast.warning('La conexión es lenta. El proceso continúa en segundo plano...');
            errorCount = 0; // Reset to continue trying
          }
        }
      }, 3000); // Poll every 3 seconds (slower to reduce load)
    }
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [executionId, executing, companyId, token, onSuccess, onClose]);

  const loadEnabledAgents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/companies/${companyId}/agents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter by module type and enabled status - flexible matching for module name variants
      const moduleTypeMap = {
        'finance': ['finance', 'finanzas', 'Finance Intelligence'],
        'finanzas': ['finance', 'finanzas', 'Finance Intelligence'],
        'revenue': ['revenue', 'Revenue'],
        'operations': ['operations', 'operaciones', 'Operaciones'],
        'operaciones': ['operations', 'operaciones', 'Operaciones'],
        'hr': ['hr', 'rrhh', 'RRHH', 'Human Resources'],
        'rrhh': ['hr', 'rrhh', 'RRHH', 'Human Resources'],
        'supply_chain': ['supply_chain', 'Supply Chain'],
        'logistics': ['logistics', 'logistica', 'Logistics Intelligence'],
        'logistica': ['logistics', 'logistica', 'Logistics Intelligence'],
        'marketing': ['marketing', 'Marketing'],
      };
      
      const matchingTypes = moduleTypeMap[moduleType] || [moduleType];
      const filtered = response.data.filter(
        agent => matchingTypes.includes(agent.module_type) && agent.is_enabled
      );
      
      setEnabledAgents(filtered);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Error al cargar agentes');
    } finally {
      setLoading(false);
    }
  };

  const loadExecutedAgents = async () => {
    try {
      const response = await axios.get(
        `${API}/companies/${companyId}/ingestion-jobs/${jobId}/executed-agents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setExecutedAgentIds(response.data.executed_agent_ids || []);
    } catch (error) {
      console.error('Error loading executed agents:', error);
    }
  };

  const toggleAgent = (agentId) => {
    if (executing) return; // Don't allow changes during execution
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const selectAll = () => {
    if (executing) return;
    setSelectedAgents(enabledAgents.map(agent => agent.id));
  };

  const selectNone = () => {
    if (executing) return;
    setSelectedAgents([]);
  };

  const handleExecute = async () => {
    if (selectedAgents.length === 0) {
      toast.error('Selecciona al menos un agente');
      return;
    }

    setExecuting(true);
    setExecutionStatus(null);
    
    try {
      const response = await axios.post(
        `${API}/companies/${companyId}/ingestion-jobs/${jobId}/execute-agents`,
        selectedAgents,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      // New background execution response
      if (response.data.execution_id) {
        setExecutionId(response.data.execution_id);
        toast.info(`🚀 Ejecución iniciada: ${response.data.total_agents} agentes en cola`);
      } else {
        // Fallback for old synchronous response (shouldn't happen but just in case)
        toast.success(`${response.data.total_insights} insights creados exitosamente`);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        setExecuting(false);
      }
    } catch (error) {
      console.error('Error executing agents:', error);
      toast.error(error.response?.data?.detail || 'Error al ejecutar agentes');
      setExecuting(false);
    }
  };

  const getAgentTypeBadge = (agentType) => {
    const badges = {
      'functional': { label: 'Funcional', class: 'bg-blue-500' },
      'premium': { label: 'Premium', class: 'bg-purple-500' },
      'c_level': { label: 'C-Level', class: 'bg-red-500' }
    };
    return badges[agentType] || badges['functional'];
  };

  const getAgentStatusIcon = (agentId) => {
    if (!executionStatus?.agents) return null;
    
    const agentStatus = executionStatus.agents.find(a => a.id === agentId);
    if (!agentStatus) return null;
    
    switch (agentStatus.status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-400" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-slate-400" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={executing ? undefined : onClose}>
      <DialogContent className="glass-dark border-blue-500/20 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Seleccionar Agentes a Ejecutar</DialogTitle>
          <p className="text-sm text-slate-400 mt-2">
            Selecciona los agentes que deseas ejecutar con los datos ingestados. 
            Se creará un insight individual por cada agente.
          </p>
        </DialogHeader>

        {/* Execution Progress Panel */}
        {executing && executionStatus && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                <span className="font-medium text-blue-300">
                  Ejecutando agentes en background...
                </span>
              </div>
              <span className="text-sm text-blue-200">
                {executionStatus.completed || 0} / {executionStatus.total || 0}
              </span>
            </div>
            
            <Progress 
              value={executionStatus.progress || 0} 
              className="h-2 bg-slate-700"
            />
            
            {executionStatus.current_agent && (
              <div className="text-sm text-slate-300">
                <span className="text-slate-500">Procesando:</span>{' '}
                <span className="text-white font-medium">{executionStatus.current_agent}</span>
              </div>
            )}
            
            {/* Agent Status List */}
            {executionStatus.agents && executionStatus.agents.length > 0 && (
              <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                {executionStatus.agents.map((agent, idx) => (
                  <div key={agent.id} className="flex items-center gap-2 text-xs">
                    {agent.status === 'running' && <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
                    {agent.status === 'completed' && <CheckCircle className="w-3 h-3 text-green-400" />}
                    {agent.status === 'error' && <XCircle className="w-3 h-3 text-red-400" />}
                    {agent.status === 'pending' && <Clock className="w-3 h-3 text-slate-500" />}
                    <span className={`
                      ${agent.status === 'running' ? 'text-blue-300' : ''}
                      ${agent.status === 'completed' ? 'text-green-300' : ''}
                      ${agent.status === 'error' ? 'text-red-300' : ''}
                      ${agent.status === 'pending' ? 'text-slate-500' : ''}
                    `}>
                      {agent.name}
                    </span>
                    {agent.error && (
                      <span className="text-red-400 truncate" title={agent.error}>
                        - {agent.error.substring(0, 30)}...
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Success/Error Summary */}
            {executionStatus.status === 'completed' && (
              <div className="flex items-center gap-4 pt-2 border-t border-blue-500/20">
                {executionStatus.insights_created?.length > 0 && (
                  <div className="flex items-center gap-1 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    {executionStatus.insights_created.length} insights creados
                  </div>
                )}
                {executionStatus.errors?.length > 0 && (
                  <div className="flex items-center gap-1 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {executionStatus.errors.length} errores
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : enabledAgents.length === 0 ? (
            <div className="p-8 text-center">
              <XCircle className="w-12 h-12 mx-auto mb-4 text-slate-500" />
              <p className="text-slate-400">No hay agentes habilitados para este módulo</p>
            </div>
          ) : (
            <>
              {/* Selection controls */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="text-sm text-slate-300">
                  {selectedAgents.length} de {enabledAgents.length} agentes seleccionados
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={selectAll}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={executing}
                  >
                    Seleccionar Todos
                  </Button>
                  <Button
                    onClick={selectNone}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={executing}
                  >
                    Ninguno
                  </Button>
                </div>
              </div>

              {/* Agent list */}
              <div className="space-y-2">
                {enabledAgents.map((agent) => {
                  const isSelected = selectedAgents.includes(agent.id);
                  const wasExecuted = executedAgentIds.includes(agent.id);
                  const typeBadge = getAgentTypeBadge(agent.agent_type);
                  const statusIcon = getAgentStatusIcon(agent.id);

                  return (
                    <div
                      key={agent.id}
                      className={`p-4 rounded-lg border transition-all ${executing ? 'cursor-default' : 'cursor-pointer'} ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-500/10' 
                          : 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/50'
                      }`}
                      onClick={() => toggleAgent(agent.id)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleAgent(agent.id)}
                          className="mt-1"
                          disabled={executing}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white">{agent.name}</h4>
                            <Badge className={`${typeBadge.class} text-white text-xs`}>
                              {typeBadge.label}
                            </Badge>
                            {wasExecuted && !executing && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Ya ejecutado
                              </Badge>
                            )}
                            {statusIcon && (
                              <span className="ml-auto">{statusIcon}</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400">{agent.description}</p>
                          {agent.usage_count > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              Usado {agent.usage_count} veces
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700">
          <Button
            onClick={onClose}
            variant="ghost"
            className="text-slate-300"
            disabled={executing}
          >
            {executing ? 'Ejecutando...' : 'Cancelar'}
          </Button>
          <Button
            onClick={handleExecute}
            className="btn-gradient"
            disabled={executing || selectedAgents.length === 0 || loading}
          >
            {executing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Procesando {executionStatus?.completed || 0}/{executionStatus?.total || selectedAgents.length}...
              </>
            ) : (
              `Ejecutar ${selectedAgents.length} Agente${selectedAgents.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentSelectorModal;
